import React, { useEffect, useRef } from "react";
import { AddressSchema } from "./validation";
import { Formik } from "formik";
import { Container, FieldErrorMessage, FormGroup, InputField, Label, Line } from "./styles";
import MaskedInput from "react-text-mask";
import { cepMask, phoneMask } from "../../util/mask";
import { getAddressByCEP } from "../../services/cep";

function InitialZipCodeLoader({ zipCode, addressAlreadyLoaded, loadAddress, formikHelpers }) {
    const requestedZipCode = useRef(null);

    useEffect(() => {
        const normalizedZipCode = String(zipCode || "").replace(/[^a-zA-Z0-9]/g, "");

        if (
            normalizedZipCode.length !== 8 ||
            addressAlreadyLoaded ||
            requestedZipCode.current === normalizedZipCode
        ) {
            return;
        }

        requestedZipCode.current = normalizedZipCode;
        loadAddress(normalizedZipCode, formikHelpers).then((loaded) => {
            if (loaded) {
                formikHelpers.validateForm();
            }
        });
    }, [zipCode, addressAlreadyLoaded, loadAddress, formikHelpers]);

    return null;
}

export default function AddressForm({ formRef, address: { id, contactName, contactPhone, zipCode, state, city, neighborhood, number, street, complement, ibge }, onAddressSubmit, showOverlay }) {
    const [isCepValidated, setIsCepValidated] = React.useState(Boolean(state || city || street || neighborhood));

    useEffect(() => {
        setIsCepValidated(Boolean(state || city || street || neighborhood));
    }, [state, city, street, neighborhood]);

    function clearAddressFields(setFieldValue) {
        setFieldValue("uf", "", false);
        setFieldValue("cidade", "", false);
        setFieldValue("rua", "", false);
        setFieldValue("bairro", "", false);
        setFieldValue("ibge", "", false);
    }

    function fillAddressFields(data, setFieldValue) {
        setFieldValue("uf", data.uf || "", false);
        setFieldValue("cidade", data.localidade || "", false);
        setFieldValue("rua", data.logradouro || "", false);
        setFieldValue("bairro", data.bairro || "", false);
        setFieldValue("ibge", data.ibge || "", false);
    }

    async function fetchAddressByCEPAndHandleForm(cep, formikHelpers) {
        const { setFieldValue, setFieldError, setFieldTouched } = formikHelpers;

        try {
            showOverlay(true);

            const { data } = await getAddressByCEP(cep);

            if (data.erro) {
                clearAddressFields(setFieldValue);
                setIsCepValidated(false);
                setFieldTouched("cep", true, false);
                setFieldError("cep", "O CEP informado é inexistente.");
                return false;
            }

            fillAddressFields(data, setFieldValue);
            setIsCepValidated(true);
            setFieldError("cep", undefined);
            return true;
        } catch (error) {
            clearAddressFields(setFieldValue);
            setIsCepValidated(false);
            setFieldTouched("cep", true, false);
            setFieldError("cep", "Erro ao buscar CEP. Tente novamente.");
            return false;
        } finally {
            showOverlay(false);
        }
    }

    return (
        <Formik
            ref={formRef}
            initialValues={{
                id: id || 0,
                nome_contato: contactName,
                telefone_contato: contactPhone,
                cep: zipCode,
                uf: state,
                cidade: city,
                bairro: neighborhood,
                numero: number,
                rua: street,
                complemento: complement,
                ibge: ibge,
            }}
            validationSchema={AddressSchema}
            validateOnChange={false}
            onSubmit={onAddressSubmit}
        >
            {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                handleSubmit,
                setFieldValue,
                setFieldError,
                setFieldTouched,
                validateForm,
                isValid,
            }) => (
                <Container>
                    <input type="hidden" name="id" value={values.id} />
                    <InitialZipCodeLoader
                        zipCode={zipCode}
                        addressAlreadyLoaded={Boolean(state || city || street || neighborhood)}
                        loadAddress={fetchAddressByCEPAndHandleForm}
                        formikHelpers={{
                            setFieldValue,
                            setFieldError,
                            setFieldTouched,
                            validateForm,
                        }}
                    />
                    <FormGroup>
                        <Label>Digite o CEP de entrega</Label>
                        <MaskedInput
                            invalid={errors.cep && touched.cep}
                            id="cep"
                            type="text"
                            name="cep"
                            className="mediumSize"
                            mask={cepMask}
                            onChange={async (e) => {
                                const oldCEP = (values.cep || "").replace(/[^a-zA-Z0-9]/g, "");
                                const nextCEP = e.target.value.replace(/[^a-zA-Z0-9]/g, "");

                                setIsCepValidated(false);
                                setFieldValue("cep", e.target.value, false);
                                setFieldError("cep", undefined);

                                if (nextCEP.length !== 8) {
                                    return;
                                }

                                if (oldCEP === nextCEP) {
                                    return;
                                }

                                const cepLoaded = await fetchAddressByCEPAndHandleForm(nextCEP, {
                                    setFieldValue,
                                    setFieldError,
                                    setFieldTouched
                                });

                                if (cepLoaded) {
                                    await validateForm();
                                }
                            }}
                            value={values.cep}
                            onBlur={handleBlur}
                            placeholder="Ex: _____-___"
                            render={(ref, props) => (
                                <InputField ref={(input) => ref(input)} {...props} />
                            )}
                        />
                        {errors.cep && touched.cep && (
                            <FieldErrorMessage>{errors.cep}</FieldErrorMessage>
                        )}

                    </FormGroup>

                    {isCepValidated && (
                        <>
                            <Line>
                                <FormGroup style={{ flex: 2 }}>
                                    <Label>Rua/Avenida</Label>
                                    <InputField
                                        invalid={errors.rua && touched.rua}
                                        id="rua"
                                        type="text"
                                        name="rua"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.rua}
                                        placeholder="Ex: Rua/Av. Fulano da Silva"
                                    />
                                    {errors.rua && touched.rua && (
                                        <FieldErrorMessage>{errors.rua}</FieldErrorMessage>
                                    )}
                                </FormGroup>
                                <FormGroup style={{ flex: 1 }}>
                                    <Label>Número</Label>
                                    <InputField
                                        invalid={errors.numero && touched.numero}
                                        id="numero"
                                        type="text"
                                        name="numero"
                                        className="smallSize"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.numero}
                                        placeholder="Ex: 1191 ou s/n"
                                    />
                                    {errors.numero && touched.numero && (
                                        <FieldErrorMessage>{errors.numero}</FieldErrorMessage>
                                    )}
                                </FormGroup>
                            </Line>
                            <Line>
                                <FormGroup style={{ flex: 2 }}>
                                    <Label>Cidade</Label>
                                    <InputField
                                        disabled
                                        invalid={errors.cidade && touched.cidade}
                                        id="cidade"
                                        type="text"
                                        name="cidade"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.cidade}
                                        placeholder="Ex: Tubarão"
                                    />
                                    {errors.cidade && touched.cidade && (
                                        <FieldErrorMessage>{errors.cidade}</FieldErrorMessage>
                                    )}
                                </FormGroup>
                                <FormGroup style={{ flex: 1 }}>
                                    <Label>Estado</Label>
                                    <InputField
                                        invalid={errors.uf && touched.uf}
                                        id="uf"
                                        type="text"
                                        name="uf"
                                        className="smallSize"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled
                                        value={values.uf}
                                        placeholder="Ex: Santa Catarina"
                                    />
                                    {errors.uf && touched.uf && (
                                        <FieldErrorMessage>{errors.uf}</FieldErrorMessage>
                                    )}
                                </FormGroup>
                            </Line>
                            <FormGroup>
                                <Label>Bairro</Label>
                                <InputField
                                    invalid={errors.bairro && touched.bairro}
                                    id="bairro"
                                    type="text"
                                    name="bairro"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.bairro}
                                    placeholder="Ex: Santo Antonio de Pádua"
                                />
                                {errors.bairro && touched.bairro && (
                                    <FieldErrorMessage>{errors.bairro}</FieldErrorMessage>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>Complemento</Label>
                                <InputField
                                    invalid={errors.complemento && touched.complemento}
                                    id="complemento"
                                    type="text"
                                    name="complemento"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.complemento}
                                    placeholder="Ex: Andar/Apartamento (opcional)"
                                />
                                {errors.complemento && touched.complemento && (
                                    <FieldErrorMessage>
                                        {errors.complemento}
                                    </FieldErrorMessage>
                                )}

                            </FormGroup>
                            <Line>
                                <FormGroup style={{ flex: 2 }}>
                                    <Label>Nome para contato</Label>
                                    <InputField
                                        invalid={errors.nome_contato && touched.nome_contato}
                                        id="nome_contato"
                                        type="text"
                                        name="nome_contato"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.nome_contato}
                                        placeholder="Ex: Fulano da Silva"
                                    />
                                    {errors.nome_contato && touched.nome_contato && (
                                        <FieldErrorMessage>
                                            {errors.nome_contato}
                                        </FieldErrorMessage>
                                    )}
                                </FormGroup>
                                <FormGroup style={{ flex: 1 }}>
                                    <Label>Telefone Contato</Label>
                                    <MaskedInput
                                        name="telefone_contato"
                                        type="text"
                                        placeholder="Ex: (__) ____-____"
                                        mask={phoneMask}
                                        className="smallSize"
                                        value={values.telefone_contato}
                                        invalid={
                                            errors.telefone_contato && touched.telefone_contato
                                        }
                                        id="registerNumber"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        render={(ref, props) => (
                                            <InputField ref={(input) => ref(input)} {...props} />
                                        )}
                                    />
                                    {errors.telefone_contato && touched.telefone_contato && (
                                        <FieldErrorMessage>
                                            {errors.telefone_contato}
                                        </FieldErrorMessage>
                                    )}
                                </FormGroup>
                            </Line>
                        </>
                    )}
                </Container>
            )}
        </Formik>
    );
}
